import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Building2,
  ChevronRight,
  Clock,
  FileText,
  Folder,
  HandCoins,
  History,
  type LucideIcon,
  Mail,
  Scale,
  User,
  Users,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { recentApi } from "@/api/ux";
import { cn } from "@/design-system/lib/utils";
import { relativeTime } from "./relativeTime";

export const RECENTLY_VIEWED_ICON_MAP: Record<string, LucideIcon> = {
  Broker: Users,
  Lead: User,
  Deal: HandCoins,
  Loan: HandCoins,
  CommissionClaim: HandCoins,
  Conversation: Mail,
  Document: FileText,
  Report: FileText,
  Closing: Folder,
  SPV: Building2,
  Investor: Building2,
  Vendor: Scale,
  Default: Scale,
};

export const RECENTLY_VIEWED_ROUTE_MAP: Record<string, (id: string) => string> = {
  Broker: (id) => `/brokers/${id}`,
  Lead: (id) => `/leads/${id}`,
  Deal: (id) => `/pipeline/${id}`,
  Loan: (id) => `/portfolio-admin/loans/${id}`,
  CommissionClaim: (id) => `/commissions/${id}`,
  Conversation: (id) => `/messages/${id}`,
  Document: (id) => `/documents/${id}`,
  Report: (id) => `/reports/${id}`,
  Closing: (id) => `/closing/${id}`,
  SPV: (id) => `/spv/${id}`,
  Investor: (id) => `/investors/${id}`,
  Vendor: (id) => `/portfolio-admin/vendors/${id}`,
  Default: (id) => `/portfolio-admin/loans?tab=defaults&case=${id}`,
};

export interface RecentlyViewedWidgetProps {
  /** When true (sidebar collapsed), renders icons only. */
  collapsed?: boolean;
  className?: string;
}

/**
 * UX Enhancements §13. Sidebar widget with the user's last 20 detail-page
 * visits. Collapses to an icon column when the sidebar is narrowed.
 */
export function RecentlyViewedWidget({ collapsed, className }: RecentlyViewedWidgetProps) {
  const queryClient = useQueryClient();
  const location = useLocation();
  const recent = useQuery({
    queryKey: ["recent"],
    queryFn: () => recentApi.list(),
    refetchOnWindowFocus: true,
  });

  // Detail GETs record server-side; invalidate on route change so the
  // sidebar reflects the latest view after navigation.
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["recent"] });
  }, [location.pathname, queryClient]);

  const items = recent.data ?? [];

  if (collapsed) {
    return (
      <div className={cn("flex flex-col items-center gap-space-2 py-space-2", className)}>
        <Clock size={16} strokeWidth={1.5} className="text-gold-shadow" aria-hidden />
        {items.slice(0, 5).map((item) => {
          const Icon = RECENTLY_VIEWED_ICON_MAP[item.entityType] ?? FileText;
          const route = resolveRoute(item.entityType, item.entityId);
          return (
            <Link
              key={`${item.entityType}-${item.entityId}`}
              to={route}
              title={item.entityTitle}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-white-pure/70 hover:text-white-pure hover:bg-white-pure/5"
            >
              <Icon size={16} strokeWidth={1.5} aria-hidden />
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn("px-space-4 pb-space-3", className)}>
      <div className="flex items-center gap-space-2 mb-space-1">
        <History size={14} strokeWidth={1.5} className="text-gold-shadow" aria-hidden />
        <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow">
          Recently viewed
        </div>
      </div>
      {recent.isLoading && (
        <div className="text-label text-white-pure/60">Loading…</div>
      )}
      {!recent.isLoading && items.length === 0 && (
        <p className="text-label text-white-pure/60 m-0">No recent items.</p>
      )}
      <ul className="m-0 p-0 list-none flex flex-col">
        {items.map((item) => {
          const Icon = RECENTLY_VIEWED_ICON_MAP[item.entityType] ?? FileText;
          const route = resolveRoute(item.entityType, item.entityId);
          return (
            <li key={`${item.entityType}-${item.entityId}`}>
              <Link
                to={route}
                title={item.entityTitle}
                className="flex items-center gap-space-2 py-space-1 text-small text-white-pure/80 hover:text-white-pure hover:bg-white-pure/5 rounded-sm px-space-2 -mx-space-2"
              >
                <Icon size={14} strokeWidth={1.5} className="text-gold-shadow shrink-0" aria-hidden />
                <span className="flex-1 truncate">
                  {item.entityTitle}
                  {item.isArchived && (
                    <span
                      className="ml-space-1 inline-block px-space-1 py-[1px] rounded-full text-[9px] font-display font-semibold uppercase tracking-label align-middle"
                      style={{ backgroundColor: "rgba(201, 169, 97, 0.15)", color: "#C9A961" }}
                    >
                      Archived
                    </span>
                  )}
                </span>
                <span className="text-label text-white-pure/50 shrink-0">
                  {relativeTime(item.viewedAt)}
                </span>
                <ChevronRight size={12} strokeWidth={1.5} className="text-white-pure/40 shrink-0" aria-hidden />
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function resolveRoute(entityType: string, entityId: string): string {
  const resolver = RECENTLY_VIEWED_ROUTE_MAP[entityType];
  return resolver ? resolver(entityId) : `/${entityType.toLowerCase()}/${entityId}`;
}
